import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';
import { usePartnerConfig, safeStorage } from '../../lib/partnerConfig';
import { trackPartnerEvent } from '../../lib/analytics';
import { canShowPopup, registerPopupShown, releasePopup } from '../../lib/popupManager';

const POPUP_ID = 'bimini_awareness';
const SESSION_KEY = 'bimini_popup_session_shown';
const DISMISS_KEY = 'bimini_popup_dismissed_at';
const CTA_KEY = 'bimini_popup_cta_at';
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;   // 7-day dismissal cap
const CTA_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;      // 30-day suppression after CTA click
const MIN_TIME_MS = 30 * 1000;                       // 30s AND 40% scroll
const MIN_SCROLL_PCT = 40;

const SUPPRESSED_PATHS = ['/checkout', '/cart', '/admin', '/login', '/signup', '/account', '/unsubscribe', '/auth'];

const withinWindow = (key, windowMs) => {
  const ts = parseInt(safeStorage.get(key) || '0', 10);
  return ts && Date.now() - ts < windowMs;
};

const blockedByContext = () => {
  const path = window.location.pathname;
  if (SUPPRESSED_PATHS.some((p) => path.startsWith(p))) return true;
  // Pages with an active lead magnet keep their promised flow uninterrupted.
  if (document.querySelector('[data-magnet-form], .na-lead-log, [data-lead-magnet]')) return true;
  // Another modal/popup already open (PeptiQ, HYROX program, APR, lead gates…).
  if (document.querySelector('[role="dialog"]:not([data-bimini-popup]), [data-testid="content-popup-overlay"], [data-testid*="promo-popup"]')) return true;
  // User is mid-form.
  const el = document.activeElement;
  if (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true;
  return false;
};

export const BiminiAwarenessPopup = ({ contextId = 'general' }) => {
  const cfg = usePartnerConfig('bimini');
  const [visible, setVisible] = useState(false);
  const closedRef = useRef(false);
  const maxScrollRef = useRef(0);
  const mountTimeRef = useRef(Date.now());
  const closeBtnRef = useRef(null);
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const suppressed = useCallback(() => (
    safeStorage.get(SESSION_KEY, true) === '1'
    || withinWindow(DISMISS_KEY, DISMISS_WINDOW_MS)
    || withinWindow(CTA_KEY, CTA_WINDOW_MS)
  ), []);

  const tryShow = useCallback(() => {
    if (closedRef.current || !cfg || cfg.enabled === false || cfg.popup_enabled === false) return;
    if (suppressed() || blockedByContext()) return;
    let allowed = true;
    try { allowed = canShowPopup(POPUP_ID); } catch { allowed = true; }
    if (!allowed) return;
    try { registerPopupShown(POPUP_ID); } catch { /* storage failure must not break */ }
    safeStorage.set(SESSION_KEY, '1', true);
    setVisible(true);
    trackPartnerEvent('partner_popup_view', {
      partner_id: 'bimini',
      placement_id: `popup-${contextId}`,
      page: window.location.pathname,
      experiment_version: cfg.experiment_version || 'v1',
    });
  }, [cfg, contextId, suppressed]);

  // Engagement trigger: at least 30s on page AND 40% scroll depth.
  useEffect(() => {
    if (!cfg || suppressed()) return undefined;
    mountTimeRef.current = Date.now();
    const evaluate = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
      maxScrollRef.current = Math.max(maxScrollRef.current, pct);
      if (Date.now() - mountTimeRef.current >= MIN_TIME_MS && maxScrollRef.current >= MIN_SCROLL_PCT) {
        tryShow();
      }
    };
    const onScroll = () => evaluate();
    const tick = setInterval(evaluate, 5000);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearInterval(tick); window.removeEventListener('scroll', onScroll); };
  }, [cfg, tryShow, suppressed]);

  const close = useCallback((reason = 'dismiss') => {
    closedRef.current = true;
    setVisible(false);
    try { releasePopup(POPUP_ID); } catch { /* noop */ }
    if (reason === 'cta') {
      safeStorage.set(CTA_KEY, String(Date.now()));
    } else {
      safeStorage.set(DISMISS_KEY, String(Date.now()));
      trackPartnerEvent('partner_popup_dismiss', {
        partner_id: 'bimini',
        placement_id: `popup-${contextId}`,
        page: window.location.pathname,
      });
    }
  }, [contextId]);

  // Keyboard: Escape closes while open.
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close('dismiss'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, close]);

  if (!visible || !cfg) return null;
  const copy = cfg.popup_copy || {};

  const onCta = () => {
    trackPartnerEvent('affiliate_click', {
      partner_id: 'bimini',
      placement_id: `popup-${contextId}`,
      page: window.location.pathname,
      cta: 'popup_primary',
      experiment_version: cfg.experiment_version || 'v1',
    });
    close('cta');
  };

  return (
    <div
      role="dialog"
      aria-label="Bimini recovery partner"
      data-bimini-popup="true"
      className={`fixed z-[90] bottom-0 left-0 right-0 md:left-auto md:bottom-6 md:right-6 md:w-[380px] ${reducedMotion ? '' : 'animate-in slide-in-from-bottom-4 fade-in duration-300'}`}
      data-testid="bimini-awareness-popup"
    >
      <div className="bg-zinc-950 border-t md:border border-[#00B4D8]/30 shadow-2xl p-5 md:p-6 max-h-[45vh] md:max-h-none overflow-y-auto">
        <button
          ref={closeBtnRef}
          onClick={() => close('dismiss')}
          aria-label="Close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#00B4D8] transition-colors"
          data-testid="bimini-popup-close"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="text-[#00B4D8] text-[10px] uppercase tracking-[0.25em] font-bold mb-1.5" data-testid="bimini-popup-eyebrow">
          {copy.eyebrow}
        </p>
        <h3 className="text-white text-lg md:text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {copy.heading}
        </h3>
        <p className="text-zinc-400 text-xs md:text-sm leading-relaxed mb-3">{copy.body}</p>
        <p className="text-zinc-400 text-[10px] leading-relaxed mb-4" data-testid="bimini-popup-disclosure">
          {cfg.disclosure}
        </p>
        <div className="flex flex-wrap items-center gap-2.5">
          <a
            href={cfg.destination_url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={onCta}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#00B4D8] text-black text-[11px] font-bold uppercase tracking-wider hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white transition-opacity"
            data-partner-id="bimini"
            data-placement-id={`popup-${contextId}`}
            data-testid="bimini-popup-cta"
          >
            {copy.cta_primary} <ExternalLink className="w-3 h-3" />
          </a>
          {cfg.guide_url && (
            <Link
              to={cfg.guide_url}
              onClick={() => close('cta')}
              className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-wider hover:text-white focus:outline-none focus:ring-2 focus:ring-[#00B4D8] transition-colors"
              data-testid="bimini-popup-guide"
            >
              {copy.cta_secondary}
            </Link>
          )}
          <button
            onClick={() => close('dismiss')}
            className="text-zinc-500 text-[11px] hover:text-zinc-300 underline focus:outline-none focus:ring-2 focus:ring-zinc-500 transition-colors ml-auto"
            data-testid="bimini-popup-maybe-later"
          >
            {copy.dismiss || 'Maybe later'}
          </button>
        </div>
      </div>
    </div>
  );
};
